// Stage Mail-V2 Composer (2026-07-23): RichTextEditorCoreToolbar — Mail V2 富文本工具栏。
//
// Purpose:
//   Mail V2 第一阶段工具栏 — 仅暴露后端 sanitizer allowlist 完全支持的操作。
//   不复用 BlogDraftEditorToolbar(后者有 heading dropdown / blockquote / strike 等
//   Mail V2 不需要的按钮)。
//
// 支持的操作(顺序固定):
//   - 太字 (bold)
//   - 斜体 (italic)
//   - 下線 (underline)
//   - 箇条書き (bullet list)
//   - 番号付きリスト (ordered list)
//   - リンク (link add/edit/remove)
//   - 元に戻す (undo)
//   - やり直し (redo)
//
// 不支持(Mail V2 第一阶段故意不展示):
//   - Strike / <s> — 后端 sanitizer 不在 allowlist
//   - Heading / blockquote / code block / horizontal rule
//   - 文本对齐 / 字体 / 字号 / 颜色 / highlight
//   - 内联图片 / 表情 / 表格

import { type ReactNode } from "react";
import { type Editor } from "@tiptap/react";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List as BulletIcon,
  ListOrdered as OrderedIcon,
  Link2 as LinkIcon,
  Undo2 as UndoIcon,
  Redo2 as RedoIcon,
} from "lucide-react";

export interface RichTextEditorCoreToolbarProps {
  editor: Editor | null;
  /** 整 toolbar 禁用 — send 飞行中 / delete 飞行中用 */
  disabled?: boolean;
  /** 额外的 class。 */
  className?: string;
  "data-testid"?: string;
}

function cx(...names: Array<string | false | null | undefined>): string {
  return names.filter(Boolean).join(" ");
}

/**
 * 简易 URL scheme 校验。只允许 http / https / mailto;空字符串表示移除 link。
 * 不允许 javascript: / data: / file: / vbscript: 等。
 */
function isAllowedLinkScheme(url: string): boolean {
  if (!url) return true; // 空允许,调用方会用 unsetLink
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return true;
  if (/^mailto:/i.test(trimmed)) return true;
  // TipTap extension 也只允许 protocols=["http","https","mailto"];这里做第二道把关
  return false;
}

export function RichTextEditorCoreToolbar({
  editor,
  disabled = false,
  className,
  "data-testid": dataTestId,
}: RichTextEditorCoreToolbarProps) {
  if (!editor) {
    return (
      <div
        className={cx("mv2-rte-toolbar", "mv2-rte-toolbar--empty", className)}
        role="toolbar"
        aria-label="書式ツールバー"
        data-testid={dataTestId ?? "mv2-rte-toolbar"}
      >
        <span className="mv2-rte-toolbar-empty">エディタ準備中…</span>
      </div>
    );
  }

  const t = editor;
  const effectiveDisabled = disabled || !t.isEditable;

  function setLink() {
    const previous = (t.getAttributes("link").href as string | undefined) ?? "";
    // MVP:使用 prompt 简单实现;后续可替换为共享 Link UI。
    const next = window.prompt("リンク先 URL(http(s):// または mailto:)", previous);
    if (next === null) return; // 用户取消
    const trimmed = next.trim();
    if (trimmed === "") {
      t.chain().focus().unsetLink().run();
      return;
    }
    if (!isAllowedLinkScheme(trimmed)) {
      window.alert(
        "リンクは http:// または https:// または mailto: のみ使えます。",
      );
      return;
    }
    t.chain().focus().extendMarkRange("link").setLink({ href: trimmed }).run();
  }

  interface IconBtnOpts {
    label: string;
    icon: ReactNode;
    active?: boolean;
    disabled?: boolean;
    /** aria-label / title 都用这个,允许覆盖 */
    ariaLabel?: string;
  }
  function iconBtn(onClick: () => void, opts: IconBtnOpts) {
    const active = !!opts.active;
    const isDisabled = effectiveDisabled || !!opts.disabled;
    const aria = opts.ariaLabel ?? opts.label;
    return (
      <button
        type="button"
        className={cx("mv2-rte-toolbar-btn", active && "active")}
        onMouseDown={(e) => e.preventDefault() /* keep selection */}
        onClick={() => {
          if (isDisabled) return;
          onClick();
          t.commands.focus();
        }}
        disabled={isDisabled}
        title={aria}
        aria-label={aria}
        aria-pressed={active}
        data-testid={`mv2-rte-toolbar-${opts.label}`}
      >
        <span className="mv2-rte-toolbar-icon" aria-hidden>
          {opts.icon}
        </span>
      </button>
    );
  }

  return (
    <div
      className={cx("mv2-rte-toolbar", className)}
      role="toolbar"
      aria-label="書式ツールバー"
      data-testid={dataTestId ?? "mv2-rte-toolbar"}
    >
      {iconBtn(() => t.chain().focus().undo().run(), {
        label: "元に戻す",
        icon: <UndoIcon size={14} />,
        disabled: !t.can().undo(),
      })}
      {iconBtn(() => t.chain().focus().redo().run(), {
        label: "やり直し",
        icon: <RedoIcon size={14} />,
        disabled: !t.can().redo(),
      })}
      <span className="mv2-rte-toolbar-sep" />

      {iconBtn(() => t.chain().focus().toggleBold().run(), {
        label: "太字",
        icon: <BoldIcon size={14} />,
        active: t.isActive("bold"),
      })}
      {iconBtn(() => t.chain().focus().toggleItalic().run(), {
        label: "斜体",
        icon: <ItalicIcon size={14} />,
        active: t.isActive("italic"),
      })}
      {iconBtn(() => t.chain().focus().toggleUnderline().run(), {
        label: "下線",
        icon: <UnderlineIcon size={14} />,
        active: t.isActive("underline"),
      })}
      <span className="mv2-rte-toolbar-sep" />

      {iconBtn(() => t.chain().focus().toggleBulletList().run(), {
        label: "箇条書き",
        icon: <BulletIcon size={14} />,
        active: t.isActive("bulletList"),
      })}
      {iconBtn(() => t.chain().focus().toggleOrderedList().run(), {
        label: "番号付きリスト",
        icon: <OrderedIcon size={14} />,
        active: t.isActive("orderedList"),
      })}
      <span className="mv2-rte-toolbar-sep" />

      {iconBtn(setLink, {
        label: "リンク",
        icon: <LinkIcon size={14} />,
        active: t.isActive("link"),
      })}
    </div>
  );
}

export default RichTextEditorCoreToolbar;
